/**
 * Cities API Client
 * Strapi integration for city CRUD operations
 */

import { getHttpClient } from '@spcc/core'
import type {
  City,
  CityListItem,
  CityStats,
  CitiesFilter,
  CreateCityRequest,
  UpdateCityRequest,
  PaginatedCities,
  PaginationMeta,
  CitySummary,
  RegionSummary,
  CountrySummary,
} from '../../domain/types'
import {
  CITY_ENDPOINTS,
  CITY_POPULATE,
  type CityListResponse,
  type CityResponse,
} from '../../api/api-types'
import {
  transformDtoToDomain,
  transformDtoToListItem,
  transformListResponseToDomain,
  transformToCreateApiRequest,
  transformToUpdateApiRequest,
  transformFilterToQueryParams,
} from '../../api/transformers'

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PAGE_SIZE = 20

// ============================================================================
// Helper Functions
// ============================================================================

function buildStrapiQueryString(params: Record<string, string | number | boolean>): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value))
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

function getCityPopulateParams(): Record<string, string | number | boolean> {
  return { ...CITY_POPULATE.DEFAULT }
}

function buildArabicCityPayload(data: CreateCityRequest | UpdateCityRequest): Record<string, string> {
  const out: Record<string, string> = {}
  if (data.name?.ar?.trim()) {
    out.name = data.name.ar.trim()
  }
  if (data.stateProvince?.ar?.trim()) {
    out.stateProvince = data.stateProvince.ar.trim()
  }
  return out
}

type CityArEntry = { name?: string; stateProvince?: string }

/** Fetch Arabic names for all cities. Returns map of documentId → AR fields. */
async function fetchCityArNamesMap(
  http: ReturnType<typeof getHttpClient>,
  extraFilters: Record<string, string | number | boolean> = {}
): Promise<Map<string, CityArEntry>> {
  const map = new Map<string, CityArEntry>()
  try {
    // Strip pagination from extraFilters — we always fetch all AR records (up to 500 per call)
    const safeFilters = Object.fromEntries(
      Object.entries(extraFilters).filter(([k]) => !k.startsWith('pagination'))
    )
    const qs = buildStrapiQueryString({
      locale: 'ar',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'fields[2]': 'stateProvince',
      ...safeFilters,
      'pagination[page]': '1',
      'pagination[pageSize]': '500',
    })
    const resp = await http.get<{ data: Array<{ documentId: string; name?: string; stateProvince?: string }> }>(
      `${CITY_ENDPOINTS.LIST}${qs}`
    )
    for (const item of resp.data.data) {
      if (item.documentId) map.set(item.documentId, { name: item.name || undefined, stateProvince: item.stateProvince || undefined })
    }
  } catch { /* AR entries are optional */ }
  return map
}

function mergeArIntoCityListItem(item: CityListItem, arMap: Map<string, CityArEntry>): CityListItem {
  const ar = arMap.get(item.documentId)
  if (!ar) return item
  return {
    ...item,
    name: { ...item.name, ar: ar.name || item.name.ar },
    stateProvince: item.stateProvince
      ? { ...item.stateProvince, ar: ar.stateProvince || item.stateProvince.ar }
      : item.stateProvince,
  }
}

function calculateCityStats(cities: CityListItem[]): CityStats {
  const byRegion: Record<string, number> = {}
  cities.forEach((c) => {
    if (c.regionName) {
      byRegion[c.regionName] = (byRegion[c.regionName] || 0) + 1
    }
  })

  return {
    total: cities.length,
    active: cities.filter((c) => c.isActive).length,
    inactive: cities.filter((c) => !c.isActive).length,
    byRegion,
  }
}

// ============================================================================
// Cities API
// ============================================================================

export const citiesApi = {
  /**
   * Get all cities with optional filtering
   */
  async getCities(filter?: CitiesFilter): Promise<CityListItem[]> {
    const http = getHttpClient()
    const filterParams = filter ? transformFilterToQueryParams(filter) : {}
    const queryParams = {
      ...filterParams,
      ...getCityPopulateParams(),
      locale: 'en',
    }
    const queryString = buildStrapiQueryString(queryParams)
    const url = `${CITY_ENDPOINTS.LIST}${queryString}`

    const [response, arMap] = await Promise.all([
      http.get<CityListResponse>(url),
      fetchCityArNamesMap(http, filterParams as Record<string, string | number | boolean>),
    ])
    return response.data.data.map(transformDtoToListItem).map((c) => mergeArIntoCityListItem(c, arMap))
  },

  /**
   * Get paginated cities with filtering
   */
  async getCitiesPaginated(filter?: CitiesFilter): Promise<PaginatedCities> {
    const http = getHttpClient()
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE

    const filterParams = transformFilterToQueryParams({
      ...filter,
      page,
      pageSize,
    })
    const queryParams = {
      ...filterParams,
      ...getCityPopulateParams(),
      locale: 'en',
    }
    const queryString = buildStrapiQueryString(queryParams)
    const url = `${CITY_ENDPOINTS.LIST}${queryString}`

    const [response, arMap] = await Promise.all([
      http.get<CityListResponse>(url),
      fetchCityArNamesMap(http, filterParams as Record<string, string | number | boolean>),
    ])
    const result = transformListResponseToDomain(response.data)
    return {
      ...result,
      data: result.data.map((c) => mergeArIntoCityListItem(c, arMap)),
    }
  },

  /**
   * Get single city by documentId with full relations.
   * Fetches EN locale (with relations) and AR locale in parallel so Arabic
   * fields (name.ar, stateProvince.ar) are always populated for edit forms.
   */
  async getCity(documentId: string): Promise<City | null> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({ ...getCityPopulateParams(), locale: 'en' })
    const url = `${CITY_ENDPOINTS.DETAIL(documentId)}${queryString}`
    const arQueryString = buildStrapiQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'stateProvince',
    })
    const arUrl = `${CITY_ENDPOINTS.DETAIL(documentId)}${arQueryString}`

    try {
      const [enResult, arResult] = await Promise.allSettled([
        http.get<CityResponse>(url),
        http.get<CityResponse>(arUrl),
      ])

      if (enResult.status === 'rejected') {
        const axiosError = enResult.reason as { response?: { status?: number } }
        if (axiosError.response?.status === 404) return null
        throw enResult.reason
      }

      const city = transformDtoToDomain(enResult.value.data.data)

      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data
        if (arDto) {
          if (arDto.name?.trim()) {
            city.name = { ...city.name, ar: arDto.name }
          }
          if (arDto.stateProvince?.trim()) {
            city.stateProvince = { ...(city.stateProvince ?? { en: '' }), ar: arDto.stateProvince }
          }
        }
      }

      return city
    } catch (error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) return null
      throw error
    }
  },

  /**
   * Get city statistics using pagination metadata for accurate counts
   */
  async getCityStats(): Promise<CityStats> {
    const http = getHttpClient()
    const baseParams = { 'pagination[pageSize]': 1, locale: 'en' }

    const [totalResp, activeResp] = await Promise.all([
      http.get<CityListResponse>(`${CITY_ENDPOINTS.LIST}${buildStrapiQueryString(baseParams)}`),
      http.get<CityListResponse>(`${CITY_ENDPOINTS.LIST}${buildStrapiQueryString({ ...baseParams, 'filters[isActive][$eq]': true })}`),
    ])

    const total = totalResp.data.meta.pagination.total
    const active = activeResp.data.meta.pagination.total
    return { total, active, inactive: total - active, byRegion: {} }
  },

  /**
   * Create a new city
   */
  async createCity(data: CreateCityRequest): Promise<City> {
    const http = getHttpClient()
    const requestData = transformToCreateApiRequest(data)

    const response = await http.post<CityResponse>(`${CITY_ENDPOINTS.CREATE}?locale=en`, {
      data: requestData,
    })

    const documentId = response.data.data.documentId
    const arPayload = buildArabicCityPayload(data)
    if (Object.keys(arPayload).length > 0) {
      await http.put<CityResponse>(`${CITY_ENDPOINTS.UPDATE(documentId)}?locale=ar`, {
        data: arPayload,
      })
    }

    const created = await citiesApi.getCity(documentId)
    return created!
  },

  /**
   * Update an existing city
   */
  async updateCity(documentId: string, data: UpdateCityRequest): Promise<City> {
    const http = getHttpClient()
    const updateData = transformToUpdateApiRequest(data)

    if (Object.keys(updateData).length > 0) {
      await http.put<CityResponse>(`${CITY_ENDPOINTS.UPDATE(documentId)}?locale=en`, {
        data: updateData,
      })
    }

    const arPayload = buildArabicCityPayload(data)
    if (Object.keys(arPayload).length > 0) {
      await http.put<CityResponse>(`${CITY_ENDPOINTS.UPDATE(documentId)}?locale=ar`, {
        data: arPayload,
      })
    }

    const updated = await citiesApi.getCity(documentId)
    return updated!
  },

  /**
   * Delete a city (soft delete by setting isActive to false)
   */
  async deleteCity(documentId: string): Promise<boolean> {
    const http = getHttpClient()

    try {
      await http.put<CityResponse>(`${CITY_ENDPOINTS.UPDATE(documentId)}?locale=en`, {
        data: {
          isActive: false,
        },
      })
      return true
    } catch {
      return false
    }
  },

  /**
   * Hard delete a city (permanent)
   */
  async hardDeleteCity(documentId: string): Promise<boolean> {
    const http = getHttpClient()

    try {
      await http.delete(CITY_ENDPOINTS.DELETE(documentId))
      return true
    } catch {
      return false
    }
  },

  /**
   * Export all cities as flat rows for XLSX export
   */
  async exportAll(): Promise<Record<string, string>[]> {
    const http = getHttpClient()
    // Fetch all cities (up to 500) with relations
    const queryString = buildStrapiQueryString({
      ...getCityPopulateParams(),
      locale: 'en',
      'pagination[pageSize]': 500,
    })
    const [response, arMap] = await Promise.all([
      http.get<CityListResponse>(`${CITY_ENDPOINTS.LIST}${queryString}`),
      fetchCityArNamesMap(http),
    ])
    const items = response.data.data.map(transformDtoToListItem).map((c) => mergeArIntoCityListItem(c, arMap))
    return items.map((city) => ({
      'Code': city.code,
      'Name (English)': city.name.en || '',
      'Name (Arabic)': city.name.ar || '',
      'Country': city.countryName || '',
      'State/Province (English)': city.stateProvince?.en || '',
      'State/Province (Arabic)': city.stateProvince?.ar || '',
      'Sales District': city.salesDistrict || '',
      'Region': city.regionName || '',
      'Status': city.isActive ? 'Active' : 'Inactive',
    }))
  },

  /**
   * Search cities by query
   */
  async searchCities(query: string): Promise<CityListItem[]> {
    return citiesApi.getCities({ search: query })
  },

  /**
   * Get cities by region
   */
  async getCitiesByRegion(regionId: string): Promise<CityListItem[]> {
    return citiesApi.getCities({ regionId })
  },

  /**
   * Get all cities as simple summaries (for dropdowns/multi-selects)
   */
  async getCitySummaries(): Promise<CitySummary[]> {
    const http = getHttpClient()
    const activeFilter = { 'filters[isActive][$eq]': true as unknown as boolean }
    const queryString = buildStrapiQueryString({
      ...getCityPopulateParams(),
      ...activeFilter,
      sort: 'name:asc',
      locale: 'en',
    })
    const url = `${CITY_ENDPOINTS.LIST}${queryString}`

    const [response, arMap] = await Promise.all([
      http.get<CityListResponse>(url),
      fetchCityArNamesMap(http, activeFilter as Record<string, string | number | boolean>),
    ])
    return response.data.data.map((dto) => {
      const item = mergeArIntoCityListItem(transformDtoToListItem(dto), arMap)
      return {
        id: item.id,
        documentId: item.documentId,
        code: item.code,
        name: item.name,
        isActive: item.isActive,
      }
    })
  },
}

// ============================================================================
// Regions API (for city assignment)
// ============================================================================

interface RegionDto {
  id: number
  documentId: string
  code: string
  name: string
  isActive?: boolean
}

export const regionsForCitiesApi = {
  /**
   * Get all active regions for city selection
   */
  async getActiveRegions(): Promise<RegionSummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': true,
      sort: 'name:asc',
    })
    const url = `/api/regions${queryString}`

    const response = await http.get<{ data: RegionDto[] }>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
      isActive: dto.isActive ?? true,
    }))
  },
}

// ============================================================================
// Countries API (for city assignment)
// ============================================================================

interface CountryDto {
  id: number
  documentId: string
  code: string
  name: string
  isActive?: boolean
}

export const countriesForCitiesApi = {
  /**
   * Get all active countries for city selection
   */
  async getActiveCountries(): Promise<CountrySummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': true,
      sort: 'name:asc',
    })
    const url = `/api/countries${queryString}`

    const response = await http.get<{ data: CountryDto[] }>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
      isActive: dto.isActive ?? true,
    }))
  },
}
